import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { chatNamesMatch } from '@shared/index'
import { ApiError } from '../api/http'
import {
  acceptFriend as acceptFriendApi,
  cancelFriend as cancelFriendApi,
  declineFriend as declineFriendApi,
  fetchFriends,
  removeFriend as removeFriendApi,
  requestFriend as requestFriendApi,
  type FriendEntry,
  type FriendPerson,
} from '../api/friends'
import { useAuthStore } from './auth'

export const useFriendsStore = defineStore('friends', () => {
  const friends = ref<FriendEntry[]>([])
  const incoming = ref<FriendPerson[]>([])
  const outgoing = ref<FriendPerson[]>([])
  const ready = ref(false)
  const loadError = ref(false)

  const incomingCount = computed(() => incoming.value.length)

  function token(): string | null {
    return useAuthStore().token
  }

  async function load(): Promise<void> {
    const auth = token()
    if (!auth) {
      clear()
      return
    }
    try {
      const snap = await fetchFriends(auth)
      friends.value = snap.friends ?? []
      incoming.value = snap.incoming ?? []
      outgoing.value = snap.outgoing ?? []
      loadError.value = false
      ready.value = true
    } catch {
      loadError.value = true
      ready.value = true
    }
  }

  function clear(): void {
    friends.value = []
    incoming.value = []
    outgoing.value = []
    ready.value = false
    loadError.value = false
  }

  function noteRead(username: string): void {
    friends.value = friends.value.map((row) =>
      chatNamesMatch(row.username, username) ? { ...row, unread: 0 } : row,
    )
  }

  function relation(username: string): 'friend' | 'incoming' | 'outgoing' | 'none' {
    if (friends.value.some((row) => chatNamesMatch(row.username, username))) return 'friend'
    if (incoming.value.some((row) => chatNamesMatch(row.username, username))) return 'incoming'
    if (outgoing.value.some((row) => chatNamesMatch(row.username, username))) return 'outgoing'
    return 'none'
  }

  function friendOf(username: string): FriendEntry | null {
    return friends.value.find((row) => chatNamesMatch(row.username, username)) ?? null
  }

  async function run(
    fn: (auth: string, username: string) => Promise<unknown>,
    username: string,
  ): Promise<void> {
    const auth = token()
    if (!auth) return
    await fn(auth, username)
    await load()
  }

  function errorCode(err: unknown): string {
    if (err instanceof ApiError) return err.code || 'friends_failed'
    return 'network'
  }

  return {
    friends,
    incoming,
    outgoing,
    ready,
    loadError,
    incomingCount,
    load,
    clear,
    noteRead,
    relation,
    friendOf,
    errorCode,
    request: (username: string) => run(requestFriendApi, username),
    accept: (username: string) => run(acceptFriendApi, username),
    decline: (username: string) => run(declineFriendApi, username),
    cancel: (username: string) => run(cancelFriendApi, username),
    remove: (username: string) => run(removeFriendApi, username),
  }
})
